
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FilterRule } from '@/types/adjustment-filters';
import { evaluateFilterRules } from '@/utils/adjustmentFilterUtils';

interface CostAnalysisItem {
  id: string;
  description: string;
  quantity: number;
  baseCost: number;
  finalCost: number;
  category: string;
  componentType: 'supplier_line_item' | 'internal_line_item' | 'assembly' | 'custom';
  markup: number;
  margin: number;
}

interface CategorySummary {
  categoryName: string;
  totalBaseCost: number;
  totalFinalCost: number;
  itemCount: number;
  averageMarkup: number;
  averageMargin: number;
}

interface CostAnalysisData {
  items: CostAnalysisItem[];
  categorySummaries: CategorySummary[];
  totalBaseCost: number;
  totalFinalCost: number;
  overallMarkup: number;
  overallMargin: number;
  discountPercentage: number;
  discountAmount: number;
  totalFinalCostAfterDiscount: number;
  overallMarkupAfterDiscount: number;
  overallMarginAfterDiscount: number;
}

export const useCostAnalysis = (estimateId: string) => {
  return useQuery({
    queryKey: ['costAnalysis', estimateId],
    queryFn: async (): Promise<CostAnalysisData> => {
      console.log('[useCostAnalysis] Fetching cost analysis for estimate:', estimateId);

      // Fetch estimate line items with related data
      const { data: lineItems, error } = await supabase
        .from('estimate_line_items')
        .select(`
          *,
          estimates!inner(company_id, adjustment_metadata)
        `)
        .eq('estimate_id', estimateId)
        .order('sort_order');

      if (error) {
        console.error('[useCostAnalysis] Error fetching line items:', error);
        throw error;
      }

      if (!lineItems || lineItems.length === 0) {
        return {
          items: [],
          categorySummaries: [],
          totalBaseCost: 0,
          totalFinalCost: 0,
          overallMarkup: 0,
          overallMargin: 0,
          discountPercentage: 0,
          discountAmount: 0,
          totalFinalCostAfterDiscount: 0,
          overallMarkupAfterDiscount: 0,
          overallMarginAfterDiscount: 0,
        };
      }

      // Group component IDs by type for bulk fetching
      const supplierIds: string[] = [];
      const internalIds: string[] = [];
      const assemblyIds: string[] = [];
      const customCategoryIds: string[] = [];

      for (const item of lineItems) {
        if (item.component_type === 'supplier_line_item') {
          supplierIds.push(item.component_id);
        } else if (item.component_type === 'internal_line_item') {
          internalIds.push(item.component_id);
        } else if (item.component_type === 'assembly') {
          assemblyIds.push(item.component_id);
        } else if (item.component_type === 'custom' && item.category_id) {
          customCategoryIds.push(item.category_id);
        }
      }

      // Bulk fetch all data in parallel
      const [supplierResult, internalResult, assemblyComponentsResult, customCategoriesResult] = await Promise.all([
        supplierIds.length > 0
          ? supabase
              .from('internal_line_items')
              .select('id, base_cost, category_id, supplier_categories!category_id(name)')
              .in('id', supplierIds)
          : Promise.resolve({ data: [] as any[], error: null }),

        internalIds.length > 0
          ? supabase
              .from('internal_line_items')
              .select('id, base_cost, category_id, internal_line_item_category_mappings(supplier_categories(name))')
              .in('id', internalIds)
          : Promise.resolve({ data: [] as any[], error: null }),

        assemblyIds.length > 0
          ? supabase
              .from('assembly_components')
              .select('assembly_id, quantity, component_type, component_id')
              .in('assembly_id', assemblyIds)
          : Promise.resolve({ data: [] as any[], error: null }),

        customCategoryIds.length > 0
          ? supabase
              .from('supplier_categories')
              .select('id, name')
              .in('id', customCategoryIds)
          : Promise.resolve({ data: [] as any[], error: null }),
      ]);

      // Build lookup maps
      const supplierMap = new Map<string, { base_cost: number; categoryName: string; category_id: string | null; trade_id: string | null }>();
      for (const item of supplierResult.data || []) {
        supplierMap.set(item.id, {
          base_cost: item.base_cost || 0,
          categoryName: (item as any).supplier_categories?.name || 'Uncategorized',
          category_id: item.category_id || null,
          trade_id: null,
        });
      }

      const internalMap = new Map<string, { base_cost: number; categoryName: string; category_id: string | null; trade_id: string | null }>();
      const internalFallbackCategoryIds: string[] = [];
      const internalIdsForTrade: string[] = [...internalIds];
      for (const item of internalResult.data || []) {
        let categoryName = 'Uncategorized';
        if (item.internal_line_item_category_mappings?.length > 0) {
          categoryName = item.internal_line_item_category_mappings[0]?.supplier_categories?.name || 'Uncategorized';
        } else if (item.category_id) {
          internalFallbackCategoryIds.push(item.category_id);
        }
        internalMap.set(item.id, {
          base_cost: item.base_cost || 0,
          categoryName,
          category_id: item.category_id,
          trade_id: null,
        });
      }

      // Fetch fallback categories for internal items that need them
      let fallbackCategoryMap = new Map<string, string>();
      if (internalFallbackCategoryIds.length > 0) {
        const { data: fallbackCategories } = await supabase
          .from('supplier_categories')
          .select('id, name')
          .in('id', internalFallbackCategoryIds);

        for (const cat of fallbackCategories || []) {
          fallbackCategoryMap.set(cat.id, cat.name);
        }
        // Update internal map with fallback categories
        for (const [id, info] of internalMap) {
          if (info.categoryName === 'Uncategorized' && info.category_id) {
            const name = fallbackCategoryMap.get(info.category_id);
            if (name) {
              info.categoryName = name;
            }
          }
        }
      }

      // Fetch trade mappings for internal items
      if (internalIdsForTrade.length > 0) {
        const { data: tradeMappings } = await supabase
          .from('internal_line_item_trade_mappings')
          .select('line_item_id, trade_id')
          .in('line_item_id', internalIdsForTrade);

        for (const mapping of tradeMappings || []) {
          const info = internalMap.get(mapping.line_item_id);
          if (info) {
            info.trade_id = mapping.trade_id;
          }
        }
      }

      const customCategoryMap = new Map<string, string>();
      for (const cat of customCategoriesResult.data || []) {
        customCategoryMap.set(cat.id, cat.name);
      }

      // Process assembly components - bulk fetch their sub-component base costs
      const assemblyComponents = assemblyComponentsResult.data || [];
      const asmSupplierIds = new Set<string>();
      const asmInternalIds = new Set<string>();
      for (const comp of assemblyComponents) {
        if (comp.component_type === 'supplier_line_item') asmSupplierIds.add(comp.component_id);
        else if (comp.component_type === 'internal_line_item') asmInternalIds.add(comp.component_id);
      }

      // Only fetch IDs we haven't already fetched
      const newAsmSupplierIds = [...asmSupplierIds].filter(id => !supplierMap.has(id));
      const newAsmInternalIds = [...asmInternalIds].filter(id => !internalMap.has(id));

      const [asmSupplierResult, asmInternalResult] = await Promise.all([
        newAsmSupplierIds.length > 0
          ? supabase.from('internal_line_items').select('id, base_cost').in('id', newAsmSupplierIds)
          : Promise.resolve({ data: [] as any[], error: null }),
        newAsmInternalIds.length > 0
          ? supabase.from('internal_line_items').select('id, base_cost').in('id', newAsmInternalIds)
          : Promise.resolve({ data: [] as any[], error: null }),
      ]);

      // Add to maps
      const asmBaseCostMap = new Map<string, number>();
      for (const item of asmSupplierResult.data || []) {
        asmBaseCostMap.set(item.id, item.base_cost || 0);
      }
      for (const item of asmInternalResult.data || []) {
        asmBaseCostMap.set(item.id, item.base_cost || 0);
      }
      // Also include already-fetched items
      for (const [id, info] of supplierMap) {
        if (!asmBaseCostMap.has(id)) asmBaseCostMap.set(id, info.base_cost);
      }
      for (const [id, info] of internalMap) {
        if (!asmBaseCostMap.has(id)) asmBaseCostMap.set(id, info.base_cost);
      }

      // Calculate assembly base costs
      const assemblyBaseCostMap = new Map<string, number>();
      for (const comp of assemblyComponents) {
        const compBaseCost = asmBaseCostMap.get(comp.component_id) || 0;
        const existing = assemblyBaseCostMap.get(comp.assembly_id) || 0;
        assemblyBaseCostMap.set(comp.assembly_id, existing + compBaseCost * comp.quantity);
      }

      // Extract adjustment filters for markup removal
      const adjustmentMetadataForFilters = (lineItems[0] as any)?.estimates?.adjustment_metadata;
      const adjustmentFilters: FilterRule[] = Array.isArray(adjustmentMetadataForFilters?.filters) ? adjustmentMetadataForFilters.filters : [];

      // Process all line items using the maps
      const processedItems: CostAnalysisItem[] = [];

      for (const item of lineItems) {
        let baseCost = 0;
        let category = 'Uncategorized';

        if (item.component_type === 'supplier_line_item') {
          const info = supplierMap.get(item.component_id);
          if (info) {
            baseCost = info.base_cost;
            category = info.categoryName;
          }
        } else if (item.component_type === 'internal_line_item') {
          const info = internalMap.get(item.component_id);
          if (info) {
            baseCost = info.base_cost;
            category = info.categoryName;
          }
        } else if (item.component_type === 'assembly') {
          baseCost = assemblyBaseCostMap.get(item.component_id) || 0;
          category = 'Assembly';
        } else if (item.component_type === 'custom') {
          baseCost = item.manual_unit_cost || 0;
          if (item.category_id) {
            category = customCategoryMap.get(item.category_id) || 'Custom';
          } else {
            category = 'Custom';
          }
        }

        const totalBaseCost = baseCost * item.quantity_in_base_units;
        let finalCost = item.manual_override
          ? (item.manual_total_cost || item.total_cost_snapshot)
          : item.total_cost_snapshot;

        // Apply markup removal filters: if item matches, use base cost instead of marked-up price
        if (adjustmentFilters.length > 0) {
          let filterCategoryId = item.category_id || undefined;
          let filterTradeId: string | undefined = undefined;

          if (item.component_type === 'supplier_line_item') {
            const info = supplierMap.get(item.component_id);
            if (info) {
              filterCategoryId = filterCategoryId || info.category_id || undefined;
              filterTradeId = info.trade_id || undefined;
            }
          } else if (item.component_type === 'internal_line_item') {
            const info = internalMap.get(item.component_id);
            if (info) {
              filterCategoryId = filterCategoryId || info.category_id || undefined;
              filterTradeId = info.trade_id || undefined;
            }
          }

          const matchesFilter = evaluateFilterRules(adjustmentFilters, {
            category_id: filterCategoryId,
            trade_id: filterTradeId,
            quantity_in_base_units: item.quantity_in_base_units,
            base_cost: baseCost,
          });

          if (matchesFilter) {
            finalCost = totalBaseCost;
          }
        }

        const markup = totalBaseCost > 0 ? ((finalCost - totalBaseCost) / totalBaseCost) * 100 : 0;
        const margin = finalCost > 0 ? ((finalCost - totalBaseCost) / finalCost) * 100 : 0;

        const componentType = item.component_type as 'supplier_line_item' | 'internal_line_item' | 'assembly' | 'custom';

        processedItems.push({
          id: item.id,
          description: item.description,
          quantity: item.quantity_in_base_units,
          baseCost: totalBaseCost,
          finalCost: finalCost,
          category: category,
          componentType: componentType,
          markup: markup,
          margin: margin,
        });
      }

      // Group by category and calculate summaries
      const categoryGroups = processedItems.reduce((groups, item) => {
        const cat = item.category;
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(item);
        return groups;
      }, {} as Record<string, CostAnalysisItem[]>);

      const categorySummaries: CategorySummary[] = Object.entries(categoryGroups).map(([categoryName, items]) => {
        const totalBaseCost = items.reduce((sum, item) => sum + item.baseCost, 0);
        const totalFinalCost = items.reduce((sum, item) => sum + item.finalCost, 0);
        const averageMarkup = totalBaseCost > 0 ? ((totalFinalCost - totalBaseCost) / totalBaseCost) * 100 : 0;
        const averageMargin = totalFinalCost > 0 ? ((totalFinalCost - totalBaseCost) / totalFinalCost) * 100 : 0;

        return {
          categoryName,
          totalBaseCost,
          totalFinalCost,
          itemCount: items.length,
          averageMarkup: isNaN(averageMarkup) ? 0 : averageMarkup,
          averageMargin: isNaN(averageMargin) ? 0 : averageMargin,
        };
      });

      const totalBaseCost = processedItems.reduce((sum, item) => sum + item.baseCost, 0);
      const totalFinalCost = processedItems.reduce((sum, item) => sum + item.finalCost, 0);
      const overallMarkup = totalBaseCost > 0 ? ((totalFinalCost - totalBaseCost) / totalBaseCost) * 100 : 0;
      const overallMargin = totalFinalCost > 0 ? ((totalFinalCost - totalBaseCost) / totalFinalCost) * 100 : 0;

      // Extract discount from adjustment_metadata
      const adjustmentMetadata = (lineItems[0] as any)?.estimates?.adjustment_metadata;
      const discountPercentage = (typeof adjustmentMetadata?.discountPercentage === 'number') ? adjustmentMetadata.discountPercentage : 0;
      const discountAmount = totalFinalCost * (discountPercentage / 100);
      const totalFinalCostAfterDiscount = totalFinalCost - discountAmount;
      const overallMarkupAfterDiscount = totalBaseCost > 0 ? ((totalFinalCostAfterDiscount - totalBaseCost) / totalBaseCost) * 100 : 0;
      const overallMarginAfterDiscount = totalFinalCostAfterDiscount > 0 ? ((totalFinalCostAfterDiscount - totalBaseCost) / totalFinalCostAfterDiscount) * 100 : 0;

      return {
        items: processedItems,
        categorySummaries,
        totalBaseCost,
        totalFinalCost,
        overallMarkup,
        overallMargin,
        discountPercentage,
        discountAmount,
        totalFinalCostAfterDiscount,
        overallMarkupAfterDiscount,
        overallMarginAfterDiscount,
      };
    },
    enabled: !!estimateId,
  });
};
